/**
 * HistoricalTimelinePanel — chronological list of source-cited milestones
 * (licensure, schedule changes, formulation changes, regulatory reviews).
 *
 * Conservative: only events with an `event_date` AND a `source_url` are
 * rendered. Anything else is filtered out so the timeline can never display
 * an uncited claim.
 */
import React from "react";
import { Calendar, ExternalLink } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

export default function HistoricalTimelinePanel({ timeline = [], className = "" }) {
    const events = (timeline || [])
        .filter((e) => e && e.event_date && e.title && e.source_url)
        .slice()
        .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
    if (!events.length) return null;

    return (
        <section
            data-testid="historical-timeline-panel"
            className={`bg-[#111827] border border-[#243044] rounded-xl p-6 ${className}`}
        >
            <header className="mb-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] font-medium mb-1.5">Historical timeline</p>
                <h2 className="text-xl font-semibold text-white">Documented milestones</h2>
                <p className="text-xs text-[#64748B] mt-1.5">
                    Licensure, schedule changes, formulation updates and regulatory reviews — each event linked to its primary source.
                </p>
            </header>

            <ol className="relative border-l border-[#243044] ml-2 space-y-5 pl-5">
                {events.map((e, i) => (
                    <li
                        key={`${e.event_date}-${i}`}
                        data-testid={`timeline-event-${e.event_date}`}
                        className="relative"
                    >
                        <span className="absolute -left-[26px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#56CCF2] ring-4 ring-[#111827]" />
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] mb-1.5">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(e.event_date)}</span>
                        </div>
                        <h3 className="text-sm font-semibold text-white leading-snug mb-1">{e.title}</h3>
                        {e.description ? (
                            <p className="text-xs text-[#CBD5E1] leading-relaxed mb-2">{e.description}</p>
                        ) : null}
                        <a
                            href={e.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`timeline-source-${e.event_date}`}
                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#56CCF2] hover:text-white transition-colors"
                        >
                            <ExternalLink className="w-3 h-3" />
                            {e.source_label || "Source"}
                        </a>
                    </li>
                ))}
            </ol>
        </section>
    );
}
